import { formatRegionDate } from '@mfw/components';
import { parseWireDay } from '@mfw/common';

/** US reads month-first, everyone else (EU) day-first; follows the app locale. */
const isUsRegion = (locale: string) => /^en([-_]US)?$/i.test(locale);

/**
 * A day as digits in the viewer's region: US "11/02/2026", EU "02.11.2026.".
 * The same digits mean different dates per region, so this always follows the
 * locale — use it only where a numeric day is needed (tables, fields).
 */
export const formatNumericDay = (date: Date, locale: string) => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return isUsRegion(locale) ? `${mm}/${dd}/${yyyy}` : `${dd}.${mm}.${yyyy}.`;
};

/**
 * A day with the month written out — it cannot be misread, so it is the
 * default: US "Nov 2, 2026", EU "2 Nov 2026".
 */
export const formatWrittenDay = (date: Date, locale: string) => {
  const month = date
    .toLocaleDateString(locale, { month: 'short' })
    .replace(/\./g, '');
  const name = `${month.charAt(0).toUpperCase()}${month.slice(1)}`;
  return isUsRegion(locale)
    ? `${name} ${date.getDate()}, ${date.getFullYear()}`
    : `${date.getDate()} ${name} ${date.getFullYear()}`;
};

/** Day and month without the year: US "Nov 2", EU "2 Nov". */
export const formatDayMonth = (date: Date, locale: string) => {
  const month = date
    .toLocaleDateString(locale, { month: 'short' })
    .replace(/\./g, '');
  const name = `${month.charAt(0).toUpperCase()}${month.slice(1)}`;
  return isUsRegion(locale)
    ? `${name} ${date.getDate()}`
    : `${date.getDate()} ${name}`;
};

/** A day header that names its weekday: US "Mon, Nov 2", EU "Mon, 2 Nov". */
export const formatWeekdayDayMonth = (date: Date, locale: string) =>
  `${formatRegionDate(date, locale, { weekday: 'short' })}, ${formatDayMonth(date, locale)}`;

/**
 * A civil day (the `yyyy-MM-dd` wire day) as the viewer reads it in their
 * own region: "Sep 25, 2026" / "25 Sep 2026". One place for the
 * membership card, the assign dialog's replace notice and the client
 * booking dialog, so the same day never reads two ways on one screen.
 */
export const formatCivilDay = (day: string, locale: string) =>
  formatWrittenDay(parseWireDay(day), locale);

/**
 * An instant (a class start, an offer's expiry, a cancellation deadline) as
 * the viewer reads it in their own language, on a 24-hour clock like the
 * calendar: "Sep 25, 22:15" / "25. sep 22:15". `options` choose the date
 * parts (day and short month by default); the time is always added.
 */
export const formatDayTime = (
  instant: Date,
  locale: string,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' },
) =>
  formatRegionDate(instant, locale, {
    ...options,
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
  });

/**
 * A civil day squeezed for a table cell: "Sep.07.'26" / "sep.07.'26". Month
 * name follows the viewer's language; the order and punctuation are fixed so a
 * column of them lines up.
 */
export const formatCivilDayCompact = (day: string, locale: string) => {
  const date = parseWireDay(day);
  const month = date
    .toLocaleDateString(locale, { month: 'short' })
    .replace(/\./g, '');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${month.charAt(0).toUpperCase()}${month.slice(1)}.${dd}.'${yy}`;
};
